import { useEffect } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { LandingHeader } from "./-landing-v1/LandingHeader";
import { LandingHero } from "./-landing-v1/LandingHero";
import { LandingSearch } from "./-landing-v1/LandingSearch";
import { LandingExamples } from "./-landing-v1/LandingExamples";
import { LandingFooter } from "./-landing-v1/LandingFooter";
import { ImageCard } from "./-landing-v1/cards/ImageCard";
import { CircleImageCard } from "./-landing-v1/cards/CircleImageCard";
import { QuoteCard } from "./-landing-v1/cards/QuoteCard";
import { InsightCard } from "./-landing-v1/cards/InsightCard";
import { ExtractionLink } from "./-landing-v1/cards/ExtractionLink";
import { EXAMPLE_COLUMNS } from "./-landing-v1/types";
import type {
	ImageCardData,
	CircleImageCardData,
	QuoteCardData,
	InsightCardData,
} from "./-landing-v1/types";

export const Route = createFileRoute("/dev/prototypes/landing-v1-components")({
	validateSearch: (search: Record<string, unknown>) => ({
		focus: typeof search.focus === "string" ? search.focus : undefined,
	}),
	component: LandingV1Components,
});

const FONT_HREF =
	"https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,600;1,400&family=Inter:wght@200;300;400&display=swap";

const ICONS_HREF =
	"https://fonts.googleapis.com/css2?family=Material+Symbols+Outlined:wght,FILL@100..700,0..1&display=swap";

// Pull sample data from EXAMPLE_COLUMNS
const allCards = EXAMPLE_COLUMNS.flatMap((col) => col.cards);
const imageOverlay = allCards.find(
	(c) => c.type === "image" && c.textPosition === "overlay",
) as ImageCardData;
const imageBelow = allCards.find(
	(c) => c.type === "image" && c.textPosition === "below",
) as ImageCardData;
const circleImage = allCards.find(
	(c) => c.type === "circle-image",
) as CircleImageCardData;
const quoteBlockquote = allCards.find(
	(c) => c.type === "quote" && c.variant === "blockquote",
) as QuoteCardData;
const quoteSerif = allCards.find(
	(c) => c.type === "quote" && c.variant === "serif",
) as QuoteCardData;
const insightWithAttr = allCards.find(
	(c) => c.type === "insight" && c.attribution,
) as InsightCardData;
const insightNoAttr = allCards.find(
	(c) => c.type === "insight" && !c.attribution,
) as InsightCardData;

function ShowcaseLabel({
	name,
	focus,
}: {
	name: string;
	focus: string | undefined;
}) {
	const isActive = focus === name;
	return (
		<Link
			to="/dev/prototypes/landing-v1-components"
			search={isActive ? { focus: undefined } : { focus: name }}
			className={`text-[11px] font-medium tracking-[0.2em] uppercase transition-colors ${
				isActive ? "text-gold" : "text-ivory/50 hover:text-ivory/80"
			}`}
		>
			{name}
		</Link>
	);
}

function SectionDivider({ label }: { label: string }) {
	return (
		<div className="flex items-center gap-4 pt-12 pb-4">
			<div className="bg-ivory/10 h-px flex-1" />
			<span className="text-ivory/30 text-[10px] font-medium tracking-[0.3em] uppercase">
				{label}
			</span>
			<div className="bg-ivory/10 h-px flex-1" />
		</div>
	);
}

function ShowcaseItem({
	name,
	focus,
	constrained,
	children,
}: {
	name: string;
	focus: string | undefined;
	constrained?: boolean;
	children: React.ReactNode;
}) {
	if (focus && focus !== name) return null;

	return (
		<div>
			<ShowcaseLabel name={name} focus={focus} />
			<div className={`mt-4 ${constrained ? "max-w-sm" : ""}`}>
				{children}
			</div>
		</div>
	);
}

function LandingV1Components() {
	const { focus } = Route.useSearch();

	useEffect(() => {
		const fonts = document.createElement("link");
		fonts.rel = "stylesheet";
		fonts.href = FONT_HREF;
		document.head.appendChild(fonts);

		const icons = document.createElement("link");
		icons.rel = "stylesheet";
		icons.href = ICONS_HREF;
		document.head.appendChild(icons);

		return () => {
			document.head.removeChild(fonts);
			document.head.removeChild(icons);
		};
	}, []);

	return (
		<div className="bg-warm-gray text-ivory/90 selection:bg-gold selection:text-warm-gray min-h-screen font-sans antialiased">
			<div className="mx-auto max-w-[1400px] px-6 py-12 lg:px-12">
				<div className="mb-8 flex items-center justify-between">
					<h1 className="font-display text-2xl">
						Landing v1 — Components
					</h1>
					{focus && (
						<Link
							to="/dev/prototypes/landing-v1-components"
							search={{ focus: undefined }}
							className="text-gold text-[11px] font-medium tracking-[0.2em] uppercase"
						>
							Show All
						</Link>
					)}
				</div>

				{/* Sections */}
				{(!focus ||
					[
						"LandingHeader",
						"LandingHero",
						"LandingSearch",
						"LandingExamples",
						"LandingFooter",
					].includes(focus)) && <SectionDivider label="Sections" />}

				<div className="space-y-12">
					<ShowcaseItem name="LandingHeader" focus={focus}>
						<div
							className="relative h-20 overflow-hidden"
							style={{ transform: "translateZ(0)" }}
						>
							<LandingHeader />
						</div>
					</ShowcaseItem>

					<ShowcaseItem name="LandingHero" focus={focus}>
						<LandingHero />
					</ShowcaseItem>

					<ShowcaseItem name="LandingSearch" focus={focus}>
						<LandingSearch />
					</ShowcaseItem>

					<ShowcaseItem name="LandingExamples" focus={focus}>
						<LandingExamples />
					</ShowcaseItem>

					<ShowcaseItem name="LandingFooter" focus={focus}>
						<LandingFooter />
					</ShowcaseItem>
				</div>

				{/* Cards */}
				{(!focus ||
					[
						"ImageCard — overlay",
						"ImageCard — below",
						"CircleImageCard",
						"QuoteCard — blockquote",
						"QuoteCard — serif",
						"InsightCard — with attribution",
						"InsightCard — no attribution",
						"ExtractionLink",
					].includes(focus)) && <SectionDivider label="Cards" />}

				<div className="space-y-12">
					<ShowcaseItem
						name="ImageCard — overlay"
						focus={focus}
						constrained
					>
						<ImageCard data={imageOverlay} />
					</ShowcaseItem>

					<ShowcaseItem
						name="ImageCard — below"
						focus={focus}
						constrained
					>
						<ImageCard data={imageBelow} />
					</ShowcaseItem>

					<ShowcaseItem
						name="CircleImageCard"
						focus={focus}
						constrained
					>
						<CircleImageCard data={circleImage} />
					</ShowcaseItem>

					<ShowcaseItem
						name="QuoteCard — blockquote"
						focus={focus}
						constrained
					>
						<QuoteCard data={quoteBlockquote} />
					</ShowcaseItem>

					<ShowcaseItem
						name="QuoteCard — serif"
						focus={focus}
						constrained
					>
						<QuoteCard data={quoteSerif} />
					</ShowcaseItem>

					<ShowcaseItem
						name="InsightCard — with attribution"
						focus={focus}
						constrained
					>
						<InsightCard data={insightWithAttr} />
					</ShowcaseItem>

					<ShowcaseItem
						name="InsightCard — no attribution"
						focus={focus}
						constrained
					>
						<InsightCard data={insightNoAttr} />
					</ShowcaseItem>

					<ShowcaseItem
						name="ExtractionLink"
						focus={focus}
						constrained
					>
						<ExtractionLink />
					</ShowcaseItem>
				</div>
			</div>
		</div>
	);
}
